import _ from 'lodash';
import {
  Form,
  Forms,
  FormSet,
  FormSets,
  FormSetSelected,
} from '../reducers/store';
import moment from 'moment';
import { sliceUuid } from '../components/utils';
import { StaticStatus } from '../components/Question';

import * as api from '../api';
import { FormTypes, getFormsByType } from './form';

export const allCompleted = (formSets: FormSets): boolean => {
  if (_.isEmpty(formSets)) return false;
  const hasLastPage = !!getLastPageFormSet(formSets);
  return hasLastPage || !_.some(formSets, (formSet) => !formSet.completed);
};

export const getLatestCompleted = (formSets: {
  [formSetId: string]: Pick<FormSet, 'completed'>;
}): Date | null => {
  const completed = _.values(formSets).filter((formSet) => formSet.completed);
  if (_.isEmpty(completed)) return null;
  return moment
    .max(completed.map((formSet) => moment(formSet.completed)))
    .toDate();
};

export const getFormIdFromShortOrFullFormId = ({
  formSets,
  formSetId,
  shortOrFullFormId,
}: {
  formSets: FormSets;
  formSetId?: string | null;
  shortOrFullFormId?: string | null;
}): string | undefined => {
  if (!shortOrFullFormId || !formSetId) return undefined;
  const selectedFormSet = formSets[formSetId];
  if (!selectedFormSet) return undefined;
  const formId = Object.keys(selectedFormSet?.forms).find(
    (key) => sliceUuid(key) === shortOrFullFormId || key === shortOrFullFormId,
  );
  return formId;
};

export const getFormsFromFormSets = (
  formSets?: FormSets,
  formType: FormTypes = FormTypes.Main,
): Forms => {
  const forms = formSets
    ? Object.assign({}, ..._.map(formSets, (formSet) => formSet.forms))
    : {};
  return getFormsByType(forms, formType);
};

export const getSelectedForms = (
  formSets: FormSets,
  selectedFormSetId: string,
): Forms => formSets[selectedFormSetId]?.forms || {};

export const getSelectedPracticePreference = (store: FormSetSelected) =>
  store.formSets[store.selectedFormSetId]?.practicePreference ||
  store.masterPracticePreference;

export const getLatestValueOfMatching = (
  formSets: FormSets,
  matcher: (
    field: api.PrevisitFormField.PrevisitFormFieldWithConfig,
  ) => boolean,
): string => {
  const flatForms = _.flatMap(Object.values(formSets), (sets) =>
    Object.values(sets.forms),
  );
  const formFields = _.flatMap(flatForms, (forms) => forms.fields);

  const matchingFields = formFields.filter((field) => matcher(field));

  const [latest] = _.orderBy(matchingFields, 'updated', 'desc');
  return latest?.value ?? '';
};

export const getLastPageFormSet = (formSets: FormSets): FormSet | undefined => {
  return Object.values(formSets).find((set) =>
    Object.values(set.forms).find((form) =>
      form.fields.find(
        (f) => f.value === StaticStatus.LastPage && f.lastPage === true,
      ),
    ),
  );
};

export const getLastPageForm = (formSets: FormSets): Form | undefined => {
  for (const set of Object.values(formSets)) {
    const form = Object.values(set.forms).find((form) =>
      form.fields.find(
        (f) => f.value === StaticStatus.LastPage && f.lastPage === true,
      ),
    );
    if (form) return form;
  }
};

export const getLastPageFormField = (
  formSets: FormSets,
): api.PrevisitFormField.PrevisitFormFieldWithConfig | undefined => {
  for (const set of Object.values(formSets)) {
    for (const form of Object.values(set.forms)) {
      const field = form.fields.find(
        (f) => f.value === StaticStatus.LastPage && f.lastPage === true,
      );
      if (field) return field;
    }
  }
};

export const findMaybeOneReviewable = (forms: Forms) =>
  Object.values(forms).find(
    (form) =>
      form.isReviewable && form.status !== api.PrevisitForm.Status.Completed,
  );
